import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { formatLocal } from '../shared/dates';
import { getUserTimeZone } from '../shared/timezone';

/**
 * ReportDataTable - Displays report data in a table with optional grouping
 *
 * @param {Array} data - Report data rows
 * @param {Array} columns - Column definitions [{field, label, format}]
 * @param {Object} metadata - Report metadata {sectionBy, keyColumn, labelColumn}
 * @param {number} total - Total record count
 * @param {number} page - Current page
 * @param {number} pageSize - Page size
 * @param {function} onPageChange - Page change handler
 * @param {function} onRemoveColumn - Remove column handler
 */
const ReportDataTable = ({
  data = [],
  columns = [],
  metadata = {},
  total = 0,
  page = 1,
  pageSize = 50,
  onPageChange,
  onRemoveColumn,
}) => {
  const tz = getUserTimeZone();

  // Group data by keyColumn if sectionBy is not 'none'
  const groupedData = useMemo(() => {
    if (!metadata.sectionBy || metadata.sectionBy === 'none' || !metadata.keyColumn) {
      return null; // Return null to indicate flat display
    }

    const groups = {};
    data.forEach((row) => {
      const key = row[metadata.keyColumn] ?? 'Unknown';
      if (!groups[key]) {
        groups[key] = {
          label: row[metadata.labelColumn] ?? key,
          rows: [],
        };
      }
      groups[key].rows.push(row);
    });

    return Object.entries(groups).map(([key, group]) => ({
      key,
      label: group.label,
      rows: group.rows,
    }));
  }, [data, metadata]);

  // Format cell value based on column format
  const formatValue = (value, format) => {
    if (value === null || value === undefined) return '-';

    if (format === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (format === 'date' && value) {
      return formatLocal(value, tz, 'yyyy-MM-dd HH:mm');
    }

    return String(value);
  };

  // Pagination
  const totalPages = Math.ceil(total / pageSize);
  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, total);

  // Render table headers
  const renderHeaders = () => (
    <thead>
      <tr>
        {columns.map((col) => (
          <th key={col.field} className="text-nowrap">
            <div className="d-flex align-items-center gap-2">
              <span>{col.label}</span>
              {onRemoveColumn && (
                <button
                  type="button"
                  className="btn btn-link p-0 text-muted"
                  onClick={() => onRemoveColumn(col.field)}
                  aria-label={`Remove ${col.label}`}
                >
                  <Icon icon="mdi:close" className="text-lg" style={{ position: "relative", top: "-1px" }} />
                </button>
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );

  // Render a single data row
  const renderRow = (row, index) => (
    <tr key={index}>
      {columns.map((col) => (
        <td key={col.field}>
          {formatValue(row[col.field], col.format)}
        </td>
      ))}
    </tr>
  );

  if (columns.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 text-muted">
        No columns selected.
      </div>
    );
  }

  // No data state
  if (!data || data.length === 0) {
    return (
      <div
        className="table-responsive"
        style={{ overflowX: 'auto' }}
      >
        <table className="table bordered-table mb-0">
          {renderHeaders()}
          <tbody>
            <tr>
              <td colSpan={columns.length} className="text-center py-4 text-muted">
                No data found for the selected criteria
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      <div
        className="table-responsive"
        style={{ overflowX: 'auto' }}
      >
        {/* Flat display (no grouping) */}
        {!groupedData && (
          <table className="table bordered-table mb-0">
            {renderHeaders()}
            <tbody>
              {data.map((row, index) => renderRow(row, index))}
            </tbody>
          </table>
        )}

        {/* Grouped display */}
        {groupedData && groupedData.map((group) => (
          <div key={group.key} className="mb-4">
            <h6 className="bg-white px-3 py-8 mb-0 text-xl d-flex align-items-center">
              <span>{group.label}</span>
              <span
                className="badge bg-primary ms-auto text-sm"
                style={{ position: 'relative', top: '-2px' }}
              >
                {group.rows.length}
              </span>
            </h6>
            <table className="table bordered-table mb-0">
              {renderHeaders()}
              <tbody>
                {group.rows.map((row, index) => renderRow(row, index))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="d-flex justify-content-between align-items-center p-3 border-top">
          <div className="text-muted">
            Showing {startRecord} to {endRecord} of {total} records
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link text-md"
                  onClick={() => onPageChange?.(page - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </button>
              </li>
              {/* Show page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link text-md"
                      onClick={() => onPageChange?.(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link text-md"
                  onClick={() => onPageChange?.(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ReportDataTable;
